// HotelSelectorBlock — the pill trigger + property-picker dropdown of
// the HomeBookingWidget. Autonomous UI: doesn't own state, receives
// `open`, `setOpen`, `blocks`, `selectedSlugs`, `onToggleSlug` and
// `summary` from its parent. Keeps the widget file leaner and gives
// us a well-defined seam for future improvements (search, filters,
// group-by-category).

import React from "react";
import { Users, ChevronDown, Pencil } from "lucide-react";
import { HOTELS } from "@/lib/data";
import { isBivouac, getAccomTerms } from "@/lib/propertyType";
import { getCampsForHotel, getCampLabels } from "@/lib/bookingCamps";
import PropertyThumb from "@/components/PropertyThumb";
import Dropdown from "@/components/booking/BookingDropdown";
import { COPY } from "@/components/booking/homeBookingWidgetCopy";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const HotelSelectorBlock = ({
  open,
  setOpen,
  summary,
  blocks,
  selectedSlugs,
  onToggleSlug,
  onEditCamp,
  lang,
}) => {
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="bg-white border border-[#2C2621]/15 hover:border-[#C16542] focus:border-[#C16542] outline-none px-3.5 py-3 text-sm text-[#2C2621] flex items-center justify-between gap-3 transition-colors min-w-[18rem]"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="home-booking-hotels-toggle"
      >
        <span className="flex items-center gap-2 truncate">
          <Users className="w-4 h-4 text-[#5C5248]" strokeWidth={1.6} />
          <span className="truncate">{summary}</span>
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.6} />
      </button>

      <Dropdown open={open} onClose={() => setOpen(false)} anchorClass="left-0" widthClass="w-[min(92vw,30rem)]">
        <div className="px-4 py-3 border-b border-[#2C2621]/10">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
            {pick(COPY.hotels_p, lang)}
          </p>
        </div>
        <ul className="max-h-[60vh] overflow-y-auto py-1.5">
          {HOTELS.filter((h) => h.bookable !== false).map((h) => {
            const checked = selectedSlugs.includes(h.slug);
            const count = blocks.filter((b) => b.slug === h.slug).length;
            const camps = getCampsForHotel(h.slug);
            const chosenCampSlugs = blocks
              .filter((b) => b.slug === h.slug && b.campSlug)
              .map((b) => b.campSlug);
            const chosenCampLabels = getCampLabels(h.slug, chosenCampSlugs, lang);
            return (
              <li key={h.slug}>
                <label
                  className="flex items-start gap-3 px-4 py-2.5 hover:bg-[#F2EBE1] cursor-pointer"
                  data-testid={`home-booking-hotel-${h.slug}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggleSlug(h.slug)}
                    className="mt-1 accent-[#C16542] cursor-pointer"
                    data-testid={`home-booking-hotel-${h.slug}-checkbox`}
                  />
                  <PropertyThumb
                    slug={h.slug}
                    alt={h.name}
                    aspectRatio="4/3"
                    className="w-14 h-11 sm:w-16 sm:h-12 shrink-0 mt-0.5"
                    testId={`home-booking-hotel-${h.slug}-thumb`}
                    cmsSlot={`booking-widget.thumb.${h.slug}`}
                  />
                  <span className="flex flex-col flex-1 min-w-0">
                    <span className="text-sm text-[#2C2621] leading-snug flex items-center gap-2">
                      <span className="truncate">{h.name}</span>
                      {count > 1 ? (
                        <span className="text-[10px] tracking-[0.18em] uppercase text-[#C16542] shrink-0">×{count}</span>
                      ) : null}
                    </span>
                    {h.location ? (
                      <span className="text-[11px] text-[#5C5248] mt-0.5 truncate">{pick(h.location, lang)}</span>
                    ) : null}
                    <span className="text-[10px] text-[#5C5248]/80 mt-0.5">
                      {isBivouac(h.slug)
                        ? getAccomTerms(h.slug, lang).units
                        : getAccomTerms(h.slug, lang).units}
                    </span>
                  </span>
                </label>
                {/* Selected-camps summary for parent-category properties.
                    Rendered outside the parent <label> so tapping the
                    "Change" link doesn't accidentally untoggle the
                    checkbox. Only visible when the property is checked,
                    has camps AND at least one camp is picked. Chosen
                    camps are joined by " · " and prefixed with an
                    accent dot for that hospitality-menu feel. */}
                {checked && camps.length > 0 && chosenCampLabels.length > 0 ? (
                  <div
                    className="pl-14 pr-4 pb-3 -mt-0.5 flex items-start gap-2"
                    data-testid={`home-booking-hotel-${h.slug}-camp-summary`}
                  >
                    <span className="text-[11px] text-[#2C2621] leading-snug flex-1 min-w-0">
                      <span className="text-[#A07042]">·</span>{" "}
                      <span className="font-medium">{chosenCampLabels.join("  ·  ")}</span>
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEditCamp?.(h.slug);
                      }}
                      data-testid={`home-booking-hotel-${h.slug}-edit-camp`}
                      className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-[#C16542] hover:text-[#a04d2f] px-1 py-0.5 transition-colors shrink-0"
                    >
                      <Pencil className="w-3 h-3" strokeWidth={1.8} />
                      {pick({ en: "Change", fr: "Modifier", es: "Cambiar" }, lang)}
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
        <div className="px-4 py-3 border-t border-[#2C2621]/10 flex items-center justify-end">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:text-[#A35133]"
            data-testid="home-booking-hotels-done"
          >
            {pick(COPY.done, lang)}
          </button>
        </div>
      </Dropdown>
    </div>
  );
};

export default HotelSelectorBlock;
